import React, { useMemo } from 'react';
import { useAppData } from '../../../providers/AppDataProvider';
import { useInvoice } from '../../invoices/contexts/InvoiceContext';
import { useBills } from '../../bills/contexts/BillsContext';
import { useEmailPanel } from '../../mail/contexts/EmailPanelContext';

/**
 * KPIStrip — dense single-line band of derived-from-hooks numbers.
 * Renders like:
 *   REV_DUE $47.3k / 18   ACTIVE 3   OVERDUE 2   UNREAD 4   DRAFTS 3
 *
 * Sources match useAttentionItems: jobs from useAppData, invoices and bills
 * from the native contexts (InvoiceContext / BillsContext). Native shapes
 * use lowercase field names (status / dueDate / amountDue).
 */
const KPIStrip: React.FC = () => {
  const { jobs } = useAppData();
  const { invoices } = useInvoice();
  const { bills } = useBills();
  const { unreadBadgeCount, userDrafts } = useEmailPanel();

  const kpis = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isUnpaid = (status: string | undefined) => {
      const s = String(status || '').toUpperCase();
      return s !== 'PAID' && s !== 'VOIDED' && s !== 'DELETED';
    };

    // Revenue due to you (unpaid invoices)
    const revDueTotal = invoices
      .filter(i => isUnpaid(i.status))
      .reduce((sum, i) => sum + (Number(i.amountDue) || 0), 0);
    const revDueCount = invoices.filter(i => isUnpaid(i.status)).length;

    // Active jobs
    const activeJobs = jobs.filter(j => j.status === 'Active').length;

    // Overdue count across invoices, bills and jobs
    const overdueInvoices = invoices.filter(i => {
      if (!isUnpaid(i.status)) return false;
      if (!i.dueDate) return false;
      return new Date(i.dueDate) < today;
    }).length;
    const overdueBills = bills.filter(b => {
      if (!isUnpaid(b.status)) return false;
      if (!b.dueDate) return false;
      return new Date(b.dueDate) < today;
    }).length;
    const overdueJobs = jobs.filter(j => {
      if (j.status === 'Closed' || j.status === 'Cancelled' || j.status === 'Archived') return false;
      if (j.status === 'Overdue') return true;
      if (!j.dueDate) return false;
      return new Date(j.dueDate) < today && (j.status === 'Open' || j.status === 'Pending');
    }).length;
    const overdueTotal = overdueInvoices + overdueBills + overdueJobs;

    // Draft count
    const draftCount = (userDrafts || []).filter(d => (d.status ?? 'draft') === 'draft').length;

    return {
      revDueTotal,
      revDueCount,
      activeJobs,
      overdueTotal,
      unread: unreadBadgeCount || 0,
      draftCount,
    };
  }, [invoices, bills, jobs, unreadBadgeCount, userDrafts]);

  const formatCurrency = (n: number): string => {
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
    return `$${Math.round(n)}`;
  };

  return (
    <div className="home-kpi">
      <div className="home-kpi__tile">
        <span className="home-kpi__label">REV_DUE</span>
        <span className="home-kpi__value">{formatCurrency(kpis.revDueTotal)}</span>
        <span className="home-kpi__meta">/ {kpis.revDueCount}</span>
      </div>
      <span className="home-kpi__sep">│</span>
      <div className="home-kpi__tile">
        <span className="home-kpi__label">ACTIVE</span>
        <span className="home-kpi__value">{kpis.activeJobs}</span>
      </div>
      <span className="home-kpi__sep">│</span>
      <div className={`home-kpi__tile ${kpis.overdueTotal > 0 ? 'home-kpi__tile--alert' : ''}`}>
        <span className="home-kpi__label">OVERDUE</span>
        <span className="home-kpi__value">{kpis.overdueTotal}</span>
      </div>
      <span className="home-kpi__sep">│</span>
      <div className="home-kpi__tile">
        <span className="home-kpi__label">UNREAD</span>
        <span className="home-kpi__value">{kpis.unread}</span>
      </div>
      <span className="home-kpi__sep">│</span>
      <div className="home-kpi__tile">
        <span className="home-kpi__label">DRAFTS</span>
        <span className="home-kpi__value">{kpis.draftCount}</span>
      </div>
    </div>
  );
};

export default KPIStrip;
